import {
  TireSeasonLabels,
  WheelLockDisagreement,
  WheelLockKeyCondition,
  WheelLockKeyLocationKind,
  WheelLockKeyMatch,
  WheelLockPresence,
  WheelLockScope,
  type TireSeason,
  type WheelLockSetupPatch,
  type WheelLockUnsupported
} from '@navio/nsdk';

import {
  WHEEL_LOCK_KEY_CONDITION_LABELS,
  WHEEL_LOCK_KEY_LOCATION_LABELS,
  WHEEL_LOCK_PRESENCE_LABELS
} from '../options/wheelLock';

/** Facts the summary and the patch share. A resolution record and a detail record both fit. */
export type WheelLockFacts = {
  presence: WheelLockPresence;
  keyCondition: WheelLockKeyCondition;
  keyLocationKind: WheelLockKeyLocationKind;
  keyLocationNote: string;
  keyMatch: WheelLockKeyMatch;
  keyMatchScope: WheelLockScope;
};

/** Text the staff editor can clear. Empty means cleared. */
type WheelLockTextFields = {
  keyLocationNote: string;
  manufacturer: string;
  notes: string;
  replacementCode: string;
};

export type WheelLockWriteFields = WheelLockFacts & WheelLockTextFields;

const LOCATION_PHRASE: Record<WheelLockKeyLocationKind, string | null> = {
  [WheelLockKeyLocationKind.GLOVEBOX]: 'in glovebox',
  [WheelLockKeyLocationKind.BOOT]: 'in the boot',
  [WheelLockKeyLocationKind.CUSTOMER]: 'with the customer',
  [WheelLockKeyLocationKind.WORKSHOP]: 'at the workshop',
  [WheelLockKeyLocationKind.UNKNOWN]: null
};

const DISAGREEMENT_TEXT: Record<WheelLockDisagreement, string> = {
  [WheelLockDisagreement.WHEELS_SHOW_LOCK]:
    'A wheel in this comparison is marked as having a lock, and this record says absent.',
  [WheelLockDisagreement.WHEELS_SHOW_NO_LOCK]:
    'The wheels in this comparison are marked as having no lock, and this record says present.',
  [WheelLockDisagreement.CAR_DECLARATION_DIFFERS]:
    "The car record disagrees with this wheel set. This wheel set's record is shown.",
  [WheelLockDisagreement.SET_DECLARATION_DIFFERS]: 'A wheel set record disagrees with the car. The car record is shown.'
};

const UNSUPPORTED_TEXT: Record<WheelLockUnsupported, string> = {
  multiple_lock_patterns: 'more than one lock pattern on a wheel set',
  photo_key_identification: 'identifying a key from a photo',
  security_scoring: 'a security score',
  product_recommendations: 'a product recommendation'
};

/** One line for a worker or a card. Available does not say the key matches. */
export const wheelLockSummary = (
  lock: Pick<WheelLockFacts, 'presence' | 'keyCondition' | 'keyLocationKind'>
): string => {
  if (lock.presence === WheelLockPresence.ABSENT) {
    return 'No wheel locks';
  }
  if (lock.presence !== WheelLockPresence.PRESENT) {
    return 'Wheel locks unknown';
  }
  if (lock.keyCondition === WheelLockKeyCondition.MISSING) {
    return 'Wheel locks reported · key missing';
  }
  if (lock.keyCondition === WheelLockKeyCondition.DAMAGED) {
    return 'Wheel locks reported · key damaged';
  }
  if (lock.keyCondition !== WheelLockKeyCondition.AVAILABLE) {
    return 'Wheel locks reported · key unknown';
  }
  const where = LOCATION_PHRASE[lock.keyLocationKind];
  return where ? `Wheel locks reported · key ${where}` : 'Wheel locks reported · key available';
};

/** Key condition plus whether that key was confirmed for this scope only. */
export const wheelLockKeyLine = (lock: WheelLockFacts): string => {
  const condition = WHEEL_LOCK_KEY_CONDITION_LABELS[lock.keyCondition];
  if (lock.keyCondition !== WheelLockKeyCondition.AVAILABLE) {
    return condition;
  }
  if (lock.keyMatch === WheelLockKeyMatch.CONFIRMED_FOR_SCOPE) {
    const scope = lock.keyMatchScope === WheelLockScope.WHEEL_SET ? 'this wheel set' : 'this car';
    return `${condition} · confirmed for ${scope}`;
  }
  if (lock.keyMatch === WheelLockKeyMatch.NOT_CONFIRMED) {
    return `${condition} · not confirmed`;
  }
  return `${condition} · match unknown`;
};

export const wheelLockLocationLine = (lock: Pick<WheelLockFacts, 'keyLocationKind' | 'keyLocationNote'>): string => {
  const kind = WHEEL_LOCK_KEY_LOCATION_LABELS[lock.keyLocationKind];
  return lock.keyLocationNote ? `${kind} · ${lock.keyLocationNote}` : kind;
};

export const wheelLockPresenceLabel = (presence: WheelLockPresence): string => WHEEL_LOCK_PRESENCE_LABELS[presence];

export const wheelLockDisagreementText = (disagreement: WheelLockDisagreement): string =>
  DISAGREEMENT_TEXT[disagreement];

/** Names every unsupported capability the response lists, in that order. */
export const wheelLockUnsupportedSentence = (unsupported: readonly WheelLockUnsupported[]): string => {
  if (unsupported.length === 0) {
    return '';
  }
  const named = unsupported.map((item) => UNSUPPORTED_TEXT[item]);
  if (named.length === 1) {
    return `Not supported: ${named[0]}.`;
  }
  const head = named.slice(0, -1).join(', ');
  return `Not supported: ${head}, and ${named[named.length - 1]}.`;
};

/** Which record the answer came from. A stored set is named so it is not read as the fitted wheels. */
export const wheelLockScopeCaption = (input: {
  scope: WheelLockScope;
  season?: TireSeason | null;
  inStorage?: boolean | null;
  beforeJob?: boolean;
}): string => {
  if (input.scope === WheelLockScope.WHEEL_SET) {
    const seasonName = input.season ? TireSeasonLabels[input.season] : 'Wheel set';
    const parts = [`${seasonName} wheel set`];
    if (input.inStorage === true) {
      parts.push('in storage');
    } else if (input.inStorage === false) {
      parts.push('on the car');
    }
    if (input.beforeJob) {
      parts.push('before this job');
    }
    return parts.join(' · ');
  }
  if (input.scope === WheelLockScope.CAR) {
    return 'Car · wheels on the car now';
  }
  return 'No recorded setup';
};

/**
 * Fields that differ. Omitted fields stay. Empty text is a clear. Same values return null.
 * A choice is never sent as null.
 */
export const wheelLockSetupPatch = (
  current: WheelLockWriteFields,
  next: WheelLockWriteFields
): WheelLockSetupPatch | null => {
  const patch: WheelLockSetupPatch = {};
  if (next.presence !== current.presence) {
    patch.presence = next.presence;
  }
  if (next.keyCondition !== current.keyCondition) {
    patch.keyCondition = next.keyCondition;
  }
  if (next.keyLocationKind !== current.keyLocationKind) {
    patch.keyLocationKind = next.keyLocationKind;
  }
  if (next.keyMatch !== current.keyMatch) {
    patch.keyMatch = next.keyMatch;
  }
  if (next.keyLocationNote !== current.keyLocationNote) {
    patch.keyLocationNote = next.keyLocationNote === '' ? null : next.keyLocationNote;
  }
  if (next.manufacturer !== current.manufacturer) {
    patch.manufacturer = next.manufacturer === '' ? null : next.manufacturer;
  }
  if (next.notes !== current.notes) {
    patch.notes = next.notes === '' ? null : next.notes;
  }
  if (next.replacementCode !== current.replacementCode) {
    patch.replacementCode = next.replacementCode === '' ? null : next.replacementCode;
  }
  return Object.keys(patch).length === 0 ? null : patch;
};

/**
 * Key match sent on a visit report.
 * A key that is not available clears a stale confirmation.
 * An available key left unknown is omitted, so the lasting record keeps its match.
 */
export const wheelLockVisitKeyMatch = (input: {
  keyCondition: WheelLockKeyCondition;
  keyMatch: WheelLockKeyMatch;
}): { keyMatch?: WheelLockKeyMatch } => {
  if (input.keyCondition !== WheelLockKeyCondition.AVAILABLE) {
    return { keyMatch: WheelLockKeyMatch.UNKNOWN };
  }
  if (input.keyMatch === WheelLockKeyMatch.UNKNOWN) {
    return {};
  }
  return { keyMatch: input.keyMatch };
};

/** Every field, for the first row of a scope. Empty text clears. A later edit uses the diff. */
export const wheelLockSetupFull = (next: WheelLockWriteFields): WheelLockSetupPatch => ({
  presence: next.presence,
  keyCondition: next.keyCondition,
  keyLocationKind: next.keyLocationKind,
  keyMatch: next.keyMatch,
  keyLocationNote: next.keyLocationNote === '' ? null : next.keyLocationNote,
  manufacturer: next.manufacturer === '' ? null : next.manufacturer,
  notes: next.notes === '' ? null : next.notes,
  replacementCode: next.replacementCode === '' ? null : next.replacementCode
});
